/**
 * AdInquiry — pricing / info request form for empty ad slots.
 *
 * Reached from Browse when a user clicks "Click here to inquire" on an
 * empty ad card. Slot context is passed via `?slot=premium-2` (or `sponsor`,
 * `nonpremium`, or absent) so we can pre-select the interest field.
 *
 * Fields: name (prefilled), email (prefilled), phone, business, interest,
 * budget/message. Submit → `POST /api/ad-inquiries` → success screen with
 * "we'll be in touch" copy.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CaretLeft, CheckCircle } from "@phosphor-icons/react";

import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function parseSlot(param) {
  // Accepts "premium-2", "sponsor-4", "nonpremium-5", "premium", "sponsor",
  // etc. Anything else falls back to "either".
  if (!param) return { type: "either", number: null };
  const raw = String(param).toLowerCase();
  const [kind, num] = raw.split("-");
  let type = "either";
  if (kind === "premium") type = "premium";
  else if (kind === "sponsor" || kind === "nonpremium") type = "sponsor";
  const n = Number(num);
  return { type, number: Number.isFinite(n) && n >= 1 && n <= 6 ? n : null };
}

export default function AdInquiry() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const querySlot = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return parseSlot(params.get("slot"));
  }, [location.search]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    slot_type: querySlot.type,
    slot_number: querySlot.number,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  // Prefill name + email from the current user (they can override).
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.display_name || user.username || "",
      email: f.email || user.email || "",
    }));
  }, [user]);

  const slotLabel = useMemo(() => {
    const t = form.slot_type;
    const n = form.slot_number;
    if (t === "either") return "Any available slot";
    const base = t === "premium" ? "Premium" : "Sponsor";
    return n ? `${base} Slot ${n}` : `${base} (any)`;
  }, [form.slot_type, form.slot_number]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    if (!form.email.trim()) { toast.error("Email is required."); return; }
    if (form.message.trim().length < 10) {
      toast.error("Please add a few sentences about what you'd like to promote.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        business_name: form.business_name.trim() || null,
        slot_type: form.slot_type,
        slot_number: form.slot_number || null,
        message: form.message.trim(),
      };
      const { data } = await api.post("/ad-inquiries", payload);
      setSubmittedId(data.inquiry_id);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-10">
        <div className="h57-surface p-8 text-center space-y-4" data-testid="ad-inquiry-success">
          <div className="flex justify-center text-[#FF5F1F]">
            <CheckCircle size={56} weight="duotone" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Inquiry Received</div>
          <h1 className="font-display font-black text-white text-2xl">Thanks — we&apos;ll be in touch.</h1>
          <p className="text-sm text-zinc-400 max-w-md mx-auto">
            Your inquiry has been logged (ref <code className="text-zinc-200">{submittedId.slice(0, 8)}</code>)
            and an admin will reach out at <span className="text-white">{form.email}</span> with pricing and next steps.
          </p>
          <div className="pt-2 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/browse")}
              data-testid="ad-inquiry-back-browse"
              className="h57-btn-primary !py-2 !px-4 text-sm"
            >
              Back to Browse
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="ad-inquiry-page">
      <Link
        to="/browse"
        className="font-mono text-[11px] tracking-widest uppercase text-zinc-500 hover:text-white inline-flex items-center gap-1 mb-4"
      >
        <CaretLeft size={12} weight="bold" /> Back to Browse
      </Link>

      <div className="h57-surface p-6 space-y-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Advertise on Hunt57</div>
          <h1 className="font-display font-black text-white text-2xl mt-1">Request Pricing</h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-lg">
            Interested in an ad spot on the Browse page? Fill out the form below — we&apos;ll get back
            to you with pricing, availability, and creative specs.
          </p>
        </div>

        <div className="border border-white/10 px-3 py-2 inline-flex items-center gap-2" data-testid="ad-inquiry-slot-badge">
          <span className="font-mono text-[9px] tracking-widest uppercase text-zinc-500">Interest</span>
          <span className="font-display font-black text-sm text-[#FFD700]">{slotLabel}</span>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="h57-label">Name *</label>
              <input
                className="h57-input"
                value={form.name}
                maxLength={120}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="ad-inquiry-name"
                required
              />
            </div>
            <div>
              <label className="h57-label">Email *</label>
              <input
                type="email"
                className="h57-input"
                value={form.email}
                maxLength={200}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="ad-inquiry-email"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="h57-label">Phone</label>
              <input
                className="h57-input"
                value={form.phone}
                maxLength={40}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="ad-inquiry-phone"
              />
            </div>
            <div>
              <label className="h57-label">Business / Store name</label>
              <input
                className="h57-input"
                value={form.business_name}
                maxLength={200}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                data-testid="ad-inquiry-business"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
            <div>
              <label className="h57-label">Which slot type?</label>
              <select
                className="h57-input"
                value={form.slot_type}
                onChange={(e) => setForm({ ...form, slot_type: e.target.value, slot_number: null })}
                data-testid="ad-inquiry-slot-type"
              >
                <option value="either">Either — best available</option>
                <option value="premium">Premium (featured, always visible)</option>
                <option value="sponsor">Sponsor (rotating)</option>
              </select>
            </div>
            {form.slot_type !== "either" && (
              <div>
                <label className="h57-label">Slot #</label>
                <select
                  className="h57-input"
                  value={form.slot_number || ""}
                  onChange={(e) => setForm({ ...form, slot_number: e.target.value ? Number(e.target.value) : null })}
                  data-testid="ad-inquiry-slot-number"
                >
                  <option value="">Any</option>
                  {Array.from({ length: form.slot_type === "premium" ? 4 : 6 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{`Slot ${i + 1}`}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="h57-label">Tell us about your ad *</label>
            <textarea
              className="h57-input"
              rows={6}
              value={form.message}
              maxLength={4000}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="What are you promoting? Any specific dates or campaigns? Rough budget?"
              data-testid="ad-inquiry-message"
              required
            />
            <div className="mt-1 font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
              {form.message.length} / 4000
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate("/browse")}
              className="h57-btn-secondary !py-2 !px-4 text-sm"
              data-testid="ad-inquiry-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="h57-btn-primary !py-2 !px-4 text-sm disabled:opacity-40"
              data-testid="ad-inquiry-submit"
            >
              {submitting ? "Sending…" : "Send Inquiry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
